import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useOutlet } from '../../context/OutletContext';
import './AdminTerminalManagement.css';

const TERMINAL_TYPES = {
  BILLING: 'billing',
  KOT: 'kot',
  DISPLAY: 'display'
};

const TERMINAL_STATUS = {
  ACTIVE: 'active',
  IDLE: 'idle',
  OFFLINE: 'offline',
  MAINTENANCE: 'maintenance'
};

const AdminTerminalManagement = () => {
  const { outlets, selectedOutlet } = useOutlet();
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: TERMINAL_TYPES.BILLING,
    outletId: '',
    location: '',
    description: ''
  });

  // Load terminals
  useEffect(() => {
    console.log('📡 Loading terminals...');

    let q;
    if (selectedOutlet) {
      // Filter by selected outlet
      q = query(
        collection(db, 'terminals'),
        where('outletId', '==', selectedOutlet.id)
      );
    } else {
      // Load all terminals
      q = collection(db, 'terminals');
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const terminalsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log(`📊 Loaded ${terminalsData.length} terminals`);
      setTerminals(terminalsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedOutlet]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTerminal) {
        // Update existing terminal
        await updateDoc(doc(db, 'terminals', editingTerminal.id), {
          ...formData,
          updatedAt: Timestamp.now()
        });
        alert('Terminal updated successfully!');
      } else {
        // Create new terminal
        const terminalNumber = `T${Date.now().toString().slice(-6)}`;
        await addDoc(collection(db, 'terminals'), {
          ...formData,
          terminalNumber,
          status: TERMINAL_STATUS.IDLE,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          lastHeartbeat: Timestamp.now()
        });
        alert(`Terminal created successfully! Terminal Number: ${terminalNumber}`);
      }

      setShowAddModal(false);
      setEditingTerminal(null);
      setFormData({
        name: '',
        type: TERMINAL_TYPES.BILLING,
        outletId: '',
        location: '',
        description: ''
      });
    } catch (error) {
      console.error('Error saving terminal:', error);
      alert('Failed to save terminal: ' + error.message);
    }
  };

  const handleEdit = (terminal) => {
    setEditingTerminal(terminal);
    setFormData({
      name: terminal.name,
      type: terminal.type,
      outletId: terminal.outletId,
      location: terminal.location || '',
      description: terminal.description || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (terminalId) => {
    if (window.confirm('Are you sure you want to delete this terminal?')) {
      try {
        await deleteDoc(doc(db, 'terminals', terminalId));
        alert('Terminal deleted successfully!');
      } catch (error) {
        console.error('Error deleting terminal:', error);
        alert('Failed to delete terminal');
      }
    }
  };

  const handleStatusChange = async (terminalId, newStatus) => {
    try {
      await updateDoc(doc(db, 'terminals', terminalId), {
        status: newStatus,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating terminal status:', error);
      alert('Failed to update terminal status');
    }
  };

  const getOutletName = (outletId) => {
    const outlet = outlets.find(o => o.id === outletId);
    return outlet ? outlet.name : 'Unknown Outlet';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TERMINAL_STATUS.ACTIVE:
        return '#10b981';
      case TERMINAL_STATUS.IDLE:
        return '#f59e0b';
      case TERMINAL_STATUS.OFFLINE:
        return '#ef4444';
      case TERMINAL_STATUS.MAINTENANCE:
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const getStats = () => {
    return {
      total: terminals.length,
      active: terminals.filter(t => t.status === TERMINAL_STATUS.ACTIVE).length,
      idle: terminals.filter(t => t.status === TERMINAL_STATUS.IDLE).length,
      offline: terminals.filter(t => t.status === TERMINAL_STATUS.OFFLINE).length
    };
  };

  const stats = getStats();

  return (
    <div className="admin-terminal-management">
      <div className="page-header">
            <div>
              <h1>Terminal Management</h1>
              <p>Manage POS terminals across all outlets</p>
            </div>
            <button
              className="btn-primary"
              onClick={() => {
                setEditingTerminal(null);
                setFormData({
                  name: '',
                  type: TERMINAL_TYPES.BILLING,
                  outletId: selectedOutlet?.id || '',
                  location: '',
                  description: ''
                });
                setShowAddModal(true);
              }}
            >
              + Add Terminal
            </button>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#e0f2fe' }}>
                <span style={{ color: '#0284c7' }}>💻</span>
              </div>
              <div className="stat-info">
                <p className="stat-label">Total Terminals</p>
                <p className="stat-value">{stats.total}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#d1fae5' }}>
                <span style={{ color: '#10b981' }}>✅</span>
              </div>
              <div className="stat-info">
                <p className="stat-label">Active</p>
                <p className="stat-value">{stats.active}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#fef3c7' }}>
                <span style={{ color: '#f59e0b' }}>⏸️</span>
              </div>
              <div className="stat-info">
                <p className="stat-label">Idle</p>
                <p className="stat-value">{stats.idle}</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#fee2e2' }}>
                <span style={{ color: '#ef4444' }}>❌</span>
              </div>
              <div className="stat-info">
                <p className="stat-label">Offline</p>
                <p className="stat-value">{stats.offline}</p>
              </div>
            </div>
          </div>

          {/* Terminals List */}
          <div className="terminals-section">
            <h2>All Terminals</h2>

            {loading ? (
              <div className="loading">Loading terminals...</div>
            ) : terminals.length === 0 ? (
              <div className="empty-state">
                <p>No terminals found. Add your first terminal to get started.</p>
              </div>
            ) : (
              <div className="terminals-grid">
                {terminals.map(terminal => (
                  <div key={terminal.id} className="terminal-card">
                    <div className="terminal-header">
                      <div>
                        <h3>{terminal.name}</h3>
                        <p className="terminal-number">#{terminal.terminalNumber}</p>
                      </div>
                      <div
                        className="terminal-status-badge"
                        style={{ backgroundColor: getStatusColor(terminal.status) }}
                      >
                        {terminal.status}
                      </div>
                    </div>

                    <div className="terminal-info">
                      <div className="info-row">
                        <span className="label">Type:</span>
                        <span className="value">{terminal.type}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">Outlet:</span>
                        <span className="value">{getOutletName(terminal.outletId)}</span>
                      </div>
                      {terminal.location && (
                        <div className="info-row">
                          <span className="label">Location:</span>
                          <span className="value">{terminal.location}</span>
                        </div>
                      )}
                      {terminal.lastHeartbeat && (
                        <div className="info-row">
                          <span className="label">Last Seen:</span>
                          <span className="value">
                            {terminal.lastHeartbeat.toDate().toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="terminal-actions">
                      <select
                        value={terminal.status}
                        onChange={(e) => handleStatusChange(terminal.id, e.target.value)}
                        className="status-select"
                      >
                        <option value={TERMINAL_STATUS.ACTIVE}>Active</option>
                        <option value={TERMINAL_STATUS.IDLE}>Idle</option>
                        <option value={TERMINAL_STATUS.OFFLINE}>Offline</option>
                        <option value={TERMINAL_STATUS.MAINTENANCE}>Maintenance</option>
                      </select>
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(terminal)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(terminal.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add/Edit Modal */}
          {showAddModal && (
            <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingTerminal ? 'Edit Terminal' : 'Add New Terminal'}</h2>
                  <button
                    className="modal-close"
                    onClick={() => setShowAddModal(false)}
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="terminal-form">
                  <div className="form-group">
                    <label>Terminal Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., POS 1, Billing Counter"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      required
                    >
                      <option value={TERMINAL_TYPES.BILLING}>Billing Terminal</option>
                      <option value={TERMINAL_TYPES.KOT}>KOT Terminal</option>
                      <option value={TERMINAL_TYPES.DISPLAY}>Display Terminal</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Outlet *</label>
                    <select
                      value={formData.outletId}
                      onChange={(e) => setFormData({ ...formData, outletId: e.target.value })}
                      required
                    >
                      <option value="">Select Outlet</option>
                      {outlets.map(outlet => (
                        <option key={outlet.id} value={outlet.id}>
                          {outlet.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., Ground Floor, Main Counter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Additional notes about this terminal"
                      rows="3"
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowAddModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      {editingTerminal ? 'Update' : 'Create'} Terminal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
  );
};

export default AdminTerminalManagement;
